import { chain } from '@leprecoins/effector';
import { createForm } from '@leprecoins/effector-forms';
import { createEffect, createEvent, createStore, sample } from 'effector';
import { debounce, not, once } from 'patronum';
import { viewerModel } from 'entities/viewer/@x';
import {
	authModel,
	getAccountTreeQuery,
	AccountTreeNode as BaseAccountTreeNode,
	USER_ROLES,
	AccountTreeValue,
} from 'shared/api';
import { accountRoute } from 'shared/routing';

type AccountTreeNode = {
	id: number;
	parentId: number | null;
	opened: boolean;
	value: AccountTreeValue;
	childrenIds: number[];
};

const treeForm = createForm({
	fields: {
		name: {
			init: '',
		},
		currencies: {
			init: [] as string[],
		},
	},
});

const nameField = treeForm.fields.name;

const currenciesField = treeForm.fields.currencies;

const $nodes = createStore<Record<number, AccountTreeNode>>({});

const $accounts = createStore<AccountTreeValue[]>([]);

const $currencies = createStore<string[]>([]);

const $roots = $nodes.map(
	(nodes) => Object.values(nodes).filter((node) => node.parentId === null) ?? null,
);

const $empty = getAccountTreeQuery.$data.map((data) => data !== null && data.length === 0);

const $selectedNodes = createStore<number[]>([]);

const $singleSelectedNode = createStore<number | null>(null);

const $initialized = not(getAccountTreeQuery.$idle);

const $flatSelect = createStore<boolean>(false);

const setSelectedNodes = createEvent<number[]>();

const selectNode = createEvent<number>();

const deselectNode = createEvent<number>();

const toggleNode = createEvent<number>();

const clearSelectedNodes = createEvent();

const expandNode = createEvent<number>();

const expandToNode = createEvent<number>();

const collapseNode = createEvent<number>();

const collapseToNode = createEvent<number>();

const setFlatSelect = createEvent<boolean>();

const fetch = createEvent();

chain(setSelectedNodes, $selectedNodes);

chain(setFlatSelect, $flatSelect);

sample({
	clock: selectNode,
	source: $selectedNodes,
	filter: $flatSelect,
	fn: (nodes, id) => [...new Set([...nodes, id])],
	target: $selectedNodes,
});

sample({
	clock: selectNode,
	source: { nodes: $nodes, selected: $selectedNodes },
	filter: not($flatSelect),
	fn: ({ nodes, selected }, id) => {
		const selectedNodes = Object.values(nodes)
			.filter((node) => node.value.parents.split('.').includes(`${id}`))
			.map((node) => node.id);

		return [...new Set([...selected, ...selectedNodes])];
	},
	target: $selectedNodes,
});

sample({
	clock: deselectNode,
	source: $selectedNodes,
	filter: $flatSelect,
	fn: (nodes, id) => nodes.filter((nodeId) => nodeId !== id),
	target: $selectedNodes,
});

sample({
	clock: deselectNode,
	source: { nodes: $nodes, selected: $selectedNodes },
	filter: not($flatSelect),
	fn: ({ nodes, selected }, id) => {
		const deselectedNodes = Object.values(nodes)
			.filter((node) => node.value.parents.split('.').includes(`${id}`))
			.map((node) => node.id);

		return selected.filter((nodeId) => !deselectedNodes.includes(nodeId));
	},
	target: $selectedNodes,
});

sample({
	clock: toggleNode,
	source: $selectedNodes,
	fn: (nodes, id) =>
		nodes.includes(id) ? nodes.filter((nodeId) => nodeId !== id) : [...new Set([...nodes, id])],
	target: $selectedNodes,
});

chain(clearSelectedNodes, $selectedNodes.reinit);

sample({
	clock: $selectedNodes,
	fn: (nodes) => (nodes.length === 1 ? nodes[0] : null),
	target: $singleSelectedNode,
});

// TODO: replace with admin group route open
sample({
	clock: authModel.authorized,
	filter: authModel.$role.map((role) => role === USER_ROLES.admin || role === USER_ROLES.god),
	fn: () => ({}),
	target: getAccountTreeQuery.start,
});

sample({
	clock: viewerModel.$account,
	filter: Boolean,
	fn: ({ wallets }) => wallets.map(({ currency }) => currency),
	target: $currencies,
});

sample({
	clock: getAccountTreeQuery.finished.success,
	fn: ({ result }) => {
		const rootIds = result.map((node) => node.id);

		const bufferNodes: BaseAccountTreeNode[] = [...result];

		const nodes: Record<number, AccountTreeNode> = {};

		while (bufferNodes.length > 0) {
			const node = bufferNodes.shift();

			if (node) {
				const allParentsId = node.value.parents.split('.');

				const rootId = rootIds.find((id) => allParentsId.includes(String(id)));

				const parentsId = allParentsId.slice(allParentsId.findIndex((id) => Number(id) === rootId));

				const isHaveParents = nodes[Number(parentsId[0])] !== undefined;

				const parentId = parentsId.length > 1 && isHaveParents ? Number(parentsId.at(-1)!) : null;

				nodes[node.id] = {
					id: node.id,
					value: node.value,
					childrenIds: node.children.map((child) => child.id),
					parentId,
					opened: parentId === null,
				};

				if (node.children.length > 0) bufferNodes.push(...node.children);
			}
		}

		return nodes;
	},
	target: $nodes,
});

sample({
	clock: once($nodes),
	fn: (nodes) => Object.values(nodes).map((node) => node.value),
	target: $accounts,
});

sample({
	clock: debounce(treeForm.$values, 300),
	filter: authModel.$authorized,
	target: getAccountTreeQuery.start,
});

sample({
	clock: fetch,
	source: treeForm.$values,
	filter: authModel.$authorized,
	target: getAccountTreeQuery.start,
});

sample({
	clock: expandNode,
	source: $nodes,
	fn: (nodes, id) => {
		nodes[id] = {
			...nodes[id],
			opened: true,
		};

		return { ...nodes };
	},
	target: $nodes,
});

sample({
	clock: expandToNode,
	source: $nodes,
	fn: (nodes, id) => {
		Object.keys(nodes)
			.map(Number)
			.forEach((nodeId) => {
				if (nodes[id].value.parents.split('.').includes(String(nodeId)) && nodeId !== id) {
					nodes[nodeId] = {
						...nodes[nodeId],
						opened: true,
					};
				}
			});

		return { ...nodes };
	},
	target: $nodes,
});

sample({
	clock: collapseNode,
	source: $nodes,
	fn: (nodes, id) => {
		nodes[id] = {
			...nodes[id],
			opened: false,
		};

		return { ...nodes };
	},
	target: $nodes,
});

sample({
	clock: collapseToNode,
	source: $nodes,
	fn: (nodes, id) => {
		Object.keys(nodes)
			.map(Number)
			.forEach((nodeId) => {
				if (nodes[nodeId].value.parents.split('.').includes(String(id))) {
					nodes[nodeId] = {
						...nodes[nodeId],
						opened: false,
					};
				}
			});

		return { ...nodes };
	},
	target: $nodes,
});

sample({
	clock: collapseNode,
	source: $nodes,
	fn: (nodes, id) => {
		Object.keys(nodes)
			.map(Number)
			.forEach((nodeId) => {
				if (nodes[nodeId].value.parents.split('.').includes(String(id))) {
					nodes[nodeId] = {
						...nodes[nodeId],
						opened: false,
					};
				}
			});

		return { ...nodes };
	},
	target: $nodes,
});

sample({
	clock: getAccountTreeQuery.finished.success,
	source: $selectedNodes,
	target: createEffect((nodes: number[]) => {
		nodes.forEach((id) => expandToNode(id));
	}),
});

sample({
	clock: accountRoute.$query,
	source: accountRoute.$isOpened,
	filter: (accountRouteOpened, query) => accountRouteOpened && query.id === undefined,
	fn: () => [],
	target: setSelectedNodes,
});

export const accountTreeModel = {
	treeForm,
	currenciesField,
	nameField,
	$nodes,
	$roots,
	$empty,
	$selectedNodes,
	$singleSelectedNode,
	$accounts,
	$currencies,
	$initialized,
	$flatSelect,
	setSelectedNodes,
	selectNode,
	deselectNode,
	toggleNode,
	clearSelectedNodes,
	fetch,
	expandNode,
	expandToNode,
	collapseNode,
	setFlatSelect,
};
